import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import AdminEconomy from "./AdminEconomy";
import AdminZTokenPackages from "./AdminZTokenPackages";
import AdminSiteSettings from "./AdminSiteSettings";
import AdminPermissions from "./AdminPermissions";
import AdminNotifications from "./AdminNotifications";
import AdminAccessUi from "./AdminAccessUi";
import AdminAccessUiModals from "./AdminAccessUiModals";
import AdminFeedback from "./AdminFeedback";
import AdminGamification from "./AdminGamification";
import AdminHelp from "./AdminHelp";
import AdminEcosystemContent from "./AdminEcosystemContent";
import AdminResendCenter from "./AdminResendCenter";
import { useAdminSession } from "../context/AdminSessionContext";
import { hasPermission } from "../utils/permissionHelpers";
import WorkspaceShell from "../Components/ui/WorkspaceShell";

type PlatformTabKey =
  | "economy"
  | "z-token-packages"
  | "site-settings"
  | "permissions"
  | "notifications"
  | "access-ui"
  | "access-ui-modals"
  | "feedback"
  | "gamification"
  | "ecosystem"
  | "resend"
  | "help";

export default function AdminWorkspacePlatform() {
  const [params, setParams] = useSearchParams();
  const { session } = useAdminSession();
  const permissions = useMemo(() => session?.permissions ?? [], [session?.permissions]);

  const tabs = useMemo(
    () =>
      [
        { key: "economy", label: "Economy", allow: hasPermission(permissions, "Economy.View"), render: <AdminEconomy /> },
        {
          key: "z-token-packages",
          label: "Z-Token",
          allow: hasPermission(permissions, "ZTokens.View"),
          render: <AdminZTokenPackages />,
        },
        {
          key: "site-settings",
          label: "Site Settings",
          allow: hasPermission(permissions, "SiteSettings.Manage"),
          render: <AdminSiteSettings />,
        },
        {
          key: "permissions",
          label: "Roles & Permissions",
          allow: hasPermission(permissions, "Permissions.View"),
          render: <AdminPermissions />,
        },
        {
          key: "notifications",
          label: "Notifications",
          allow: hasPermission(permissions, "Notifications.View"),
          render: <AdminNotifications />,
        },
        {
          key: "ecosystem",
          label: "Bilgilendirme",
          allow: hasPermission(permissions, "SiteSettings.Manage"),
          render: <AdminEcosystemContent />,
        },
        {
          key: "resend",
          label: "Resend",
          allow: hasPermission(permissions, "SiteSettings.Manage"),
          render: <AdminResendCenter />,
        },
        { key: "access-ui", label: "Access UI", allow: hasPermission(permissions, "Economy.View"), render: <AdminAccessUi /> },
        {
          key: "access-ui-modals",
          label: "Access UI Modals",
          allow: hasPermission(permissions, "Economy.View"),
          render: <AdminAccessUiModals />,
        },
        { key: "feedback", label: "Feedback", allow: hasPermission(permissions, "Feedback.View"), render: <AdminFeedback /> },
        {
          key: "gamification",
          label: "Gamification",
          allow: hasPermission(permissions, "Gamification.View"),
          render: <AdminGamification />,
        },
        { key: "help", label: "Help", allow: true, render: <AdminHelp /> },
      ] as const,
    [permissions],
  );

  const available = tabs.filter((tab) => tab.allow);
  const requested = (params.get("tab") ?? "economy") as PlatformTabKey;
  const active = available.find((tab) => tab.key === requested) ?? available[0];

  if (!active) {
    return <div className="admin-soft-alert text-sm">Bu workspace icin yetkin bulunmuyor.</div>;
  }

  const setTab = (key: string) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  return (
    <WorkspaceShell
      title="Platform Workspace"
      subtitle="Sistem ayarlari, ekonomi, permission katmani ve operasyonel konfigurasyonlarin merkezi kontrol paneli."
      tabs={available.map((tab) => ({ key: tab.key, label: tab.label }))}
      activeKey={active.key}
      onTabChange={setTab}
      statPills={
        <>
          <span className="admin-pill">Konfigurasyon yonetimi</span>
          <span className="admin-pill">Site branding</span>
          <span className="admin-pill">Notification orchestration</span>
        </>
      }
    >
      {active.render}
    </WorkspaceShell>
  );
}
